/**
 * SSR data for the verified-dealer directory.
 *
 * The list used to be fetched only in the browser, so crawlers received a
 * "loading" placeholder. The route loader now fetches the same data on the
 * server and the page seeds its state from it.
 */
import { supabase } from "@/integrations/supabase/client";
import { dealerProfilePath } from '@/utils/dealerUrl';

export type VerifiedDealerSsr = {
  user_id: string;
  email: string;
  listing_count: number;
  subscription_tier: string;
  verified_since: string;
  business_name: string | null;
  dealer_id: string | null;
  is_active_subscriber: boolean;
  logo_url: string | null;
  is_verified: boolean;
  canonical_path?: string | null;
};

export async function loadVerifiedDealers(
  countryCode: string,
): Promise<{ country: string; dealers: VerifiedDealerSsr[] }> {
  try {
    const { data, error } = await supabase.rpc("get_verified_dealers_public", {
      p_country_code: countryCode,
    });
    if (error || !Array.isArray(data)) return { country: countryCode, dealers: [] };

    const dealers: VerifiedDealerSsr[] = (data as Array<Record<string, unknown>>).map((d) => ({
      user_id: String(d.user_id),
      email: (d.email as string) || "Dealer",
      listing_count: Number(d.listing_count) || 0,
      subscription_tier: (d.subscription_tier as string) || "Basic",
      verified_since: (d.verified_since as string) || "",
      business_name: (d.business_name as string) || null,
      dealer_id: (d.dealer_id as string) || null,
      is_active_subscriber: (d.is_active_subscriber as boolean) ?? true,
      logo_url: null,
      is_verified: false,
    }));

    const userIds = dealers.map((d) => d.user_id);
    if (userIds.length > 0) {
      const { data: profiles } = await supabase
        .from("dealer_profiles")
        .select("user_id, logo_url, verified, risco_verified, subdomain_alias")
        .in("user_id", userIds);
      const logoMap = new Map(
        (profiles || []).map(p => [p.user_id, p]),
      );
      dealers.forEach((d) => {
        const profile = logoMap.get(d.user_id);
        d.logo_url = profile?.logo_url || null;
        d.is_verified = profile?.verified === true || profile?.risco_verified === true;
        d.canonical_path = profile ? dealerProfilePath(profile) : null;
      });
    }

    return { country: countryCode, dealers: dealers.filter((d) => d.is_verified) };
  } catch {
    return { country: countryCode, dealers: [] };
  }
}
